'use client';

import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LoginIcon from '@mui/icons-material/Login';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import InputMask from 'react-input-mask';
import axios from 'axios';
import * as authApi from '../../../api/auth.api';

// Типизация для InputMask
const MaskedInput = InputMask as React.ComponentType<{
  mask: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  disabled: boolean;
  children: (inputProps: React.InputHTMLAttributes<HTMLInputElement>) => React.ReactElement;
}>;

const StyledForm = styled(Box)<{ component?: string }>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
  padding: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const LoginButton = styled(Button)(() => ({
  padding: '12px 24px',
  fontSize: '1rem',
  fontWeight: 500,
  borderRadius: '8px',
  textTransform: 'none',
  height: '48px',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
  },
}));

export interface LoginFormData {
  phone: string;
  password: string;
}

interface LoginFormProps {
  /**
   * Callback, вызываемый при успешной авторизации
   * Используется для закрытия модального окна
   */
  onSuccess?: () => void;
}

/**
 * Компонент формы авторизации
 * 
 * Предоставляет форму для входа в систему с полями телефона и пароля.
 * Выполняет валидацию данных и обработку ошибок.
 * После успешной авторизации вызывает callback onSuccess.
 * 
 * @param onSuccess - Callback для закрытия модального окна после успешной авторизации
 * 
 * @example
 * ```tsx
 * <LoginForm onSuccess={() => setModalOpen(false)} />
 * ```
 */
export const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
}) => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhone(value);

    if (isSubmitted) {
      validatePhoneField(value);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);

    if (isSubmitted) {
      validatePasswordField(value);
    }
  };

  const validatePhoneField = (value: string): boolean => {
    if (!value.trim()) {
      setPhoneError('Введите номер телефона');
      return false;
    }

    // Валидация формата телефона с маской
    if (!/^\+7\s\(\d{3}\)\s\d{3}-\d{2}-\d{2}$/.test(value)) {
      setPhoneError('Введите корректный номер телефона');
      return false;
    }

    setPhoneError(null);
    return true;
  };

  const validatePasswordField = (value: string): boolean => {
    if (!value) {
      setPasswordError('Введите пароль');
      return false;
    }

    if (value.length < 6) {
      setPasswordError('Пароль должен содержать минимум 6 символов');
      return false;
    }

    setPasswordError(null);
    return true;
  };

  const normalizePhone = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, '');

    if (cleaned.startsWith('8') && cleaned.length === 11) {
      return '+7' + cleaned.slice(1);
    }

    if (cleaned.startsWith('7') && cleaned.length === 11) {
      return '+' + cleaned;
    }

    if (cleaned.length === 10) {
      return '+7' + cleaned;
    }

    return '+' + cleaned;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Отмечаем что форма была отправлена
    setIsSubmitted(true);

    // Валидация
    const isPhoneValid = validatePhoneField(phone);
    const isPasswordValid = validatePasswordField(password);

    if (!isPhoneValid || !isPasswordValid) {
      return;
    }

    // Очищаем предыдущие ошибки
    setError(null);
    setIsLoading(true);

    try {
      // Вызываем API авторизации
      const response = await authApi.login(
        normalizePhone(phone.trim()),
        password
      );

      // Сохраняем токен и данные пользователя в localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('app-web-auth-token', response.token);
        localStorage.setItem('app-web-user-data', JSON.stringify(response.user));
      }

      // Вызываем callback успешной авторизации
      if (onSuccess) {
        onSuccess();
      }

      // Перезагружаем страницу для обновления состояния авторизации
      window.location.reload();
    } catch (err) {
      // Обработка ошибок авторизации (Требование 9.1)
      if (axios.isAxiosError(err)) {
        // Обработка ошибок сети (Требование 9.3)
        if (!err.response) {
          setError('Проблема с подключением. Проверьте интернет и попробуйте снова.');
        } else if (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK') {
          setError('Превышено время ожидания. Проверьте подключение к интернету.');
        } else {
          // Обработка HTTP ошибок с дружелюбными формулировками (Требование 9.4)
          switch (err.response.status) {
            case 400:
              setError('Некорректные данные. Проверьте правильность ввода.');
              break;
            case 401:
              setError('Неверный номер телефона или пароль. Попробуйте еще раз.');
              break;
            case 403:
              setError('Доступ запрещен. Обратитесь в службу поддержки.');
              break;
            case 404:
              setError('Пользователь с таким номером телефона не найден.');
              break;
            case 429:
              setError('Слишком много попыток входа. Попробуйте через несколько минут.');
              break;
            case 500:
            case 502:
            case 503:
            case 504:
              setError('Сервер временно недоступен. Попробуйте позже.');
              break;
            default:
              setError('Произошла ошибка при входе. Попробуйте позже.');
          }
        }
      } else if (err instanceof Error) {
        // Обработка других типов ошибок
        setError(err.message || 'Произошла непредвиденная ошибка. Попробуйте позже.');
      } else {
        setError('Произошла непредвиденная ошибка. Попробуйте позже.');
      }

      console.error('Ошибка авторизации:', err);
    } finally {
      setIsLoading(false);
    }
  };



  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  return (
    <StyledForm component="form" onSubmit={handleSubmit}>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontSize: { xs: '1.5rem', sm: '1.75rem' },
          textAlign: 'center',
          fontWeight: 600,
          mb: 0.5,
        }}
      >
        Вход в личный кабинет
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: 'center', mb: 2 }}
      >
        Войдите, чтобы управлять своими услугами
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <MaskedInput
        mask="+7 (999) 999-99-99"
        value={phone}
        onChange={handlePhoneChange}
        onBlur={() => {}}
        disabled={isLoading}
      >
        {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
          <TextField
            {...(inputProps as Record<string, unknown>)}
            label="Номер телефона"
            error={isSubmitted && !!phoneError}
            helperText={
              isSubmitted && phoneError
                ? phoneError
                : 'Введите номер телефона для входа'
            }
            placeholder="+7 (900) 123-45-67"
            fullWidth
            required
            autoComplete="tel"
            autoFocus
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        )}
      </MaskedInput>

      <TextField
        label="Пароль"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={handlePasswordChange}
        error={isSubmitted && !!passwordError}
        helperText={
          isSubmitted && passwordError
            ? passwordError
            : 'Минимум 6 символов'
        }
        placeholder="Введите пароль"
        fullWidth
        required
        disabled={isLoading}
        autoComplete="current-password"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="показать пароль"
                onClick={handleTogglePasswordVisibility}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
                disabled={isLoading}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <LoginButton
        type="submit"
        variant="contained"
        color="primary"
        size="large"
        disabled={isLoading || !phone || !password}
        startIcon={isLoading ? <CircularProgress size={20} /> : <LoginIcon />}
        fullWidth
      >
        {isLoading ? 'Вход...' : 'Войти'}
      </LoginButton>
    </StyledForm>
  );
};
